import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Box from '@cloudscape-design/components/box';
import Alert from '@cloudscape-design/components/alert';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import Cards from '@cloudscape-design/components/cards';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { Spinner } from '@cloudscape-design/components';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const PDFKeywordAnalyzer = forwardRef((props, ref) => {
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfText, setPdfText] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchMode, setSearchMode] = useState('exact');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Expose reset function to parent component
  useImperativeHandle(ref, () => ({
    resetAnalysis: () => {
      setPdfFile(null);
      setPdfText([]);
      setSearchTerm('');
      setSearchMode('exact');
      setResults(null);
      setLoading(false);
      setError('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
  }));

  // Read file data as array buffer
  const readFileData = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || file.type !== 'application/pdf') {
      setError('Please select a valid PDF file');
      return;
    }

    // Check file size (limit to 50MB for client-side processing)
    if (file.size > 50 * 1024 * 1024) {
      setError('File too large. Please select a PDF smaller than 50MB.');
      return;
    }

    setPdfFile(file);
    setResults(null);
    setLoading(true);
    setError('');

    try {
      // Use the readFileData function
      const data = await readFileData(file);
      const pdf = await pdfjsLib.getDocument(data).promise;

      const pages = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map((item) => item.str).join(' ');
        pages.push({
          pageNumber: i,
          text: pageText,
          wordCount: pageText.split(/\s+/).filter((word) => word.length > 0).length,
        });
      }

      setPdfText(pages);
    } catch (error) {
      console.error('Error processing PDF:', error);
      setError('Error processing PDF file: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const searchKeyword = () => {
    if (!searchTerm.trim() || pdfText.length === 0) {
      setError('Please enter a search term');
      return;
    }

    setError('');
    const searchResults = {
      keyword: searchTerm,
      searchMode: searchMode,
      totalPages: pdfText.length,
      pagesFound: [],
      totalOccurrences: 0,
      percentageOfPages: 0,
      occurrenceDensity: 0,
      wordInstances: {}, // New property to track individual word instances
    };

    let totalWords = 0;

    pdfText.forEach((page) => {
      const pageTextLower = page.text.toLowerCase();
      let occurrences = 0;
      let matches = [];
      let wordInstancesOnPage = {}; // Track instances of each word on this page

      switch (searchMode) {
        case 'exact':
          // Exact match (current behavior)
          matches = pageTextLower.match(new RegExp(escapeRegExp(searchTerm.toLowerCase()), 'g')) || [];
          occurrences = matches.length;
          break;

        case 'multiword':
          // Multi-word search with comma-separated values
          const searchTerms = searchTerm
            .toLowerCase()
            .split(',')
            .map((term) => term.trim())
            .filter((term) => term.length > 0);

          if (searchTerms.length > 0) {
            // For each search term, count its occurrences
            searchTerms.forEach((term) => {
              const termMatches = pageTextLower.match(new RegExp(`\\b${escapeRegExp(term)}\\b`, 'g')) || [];
              if (termMatches.length > 0) {
                wordInstancesOnPage[term] = termMatches.length;
                occurrences += termMatches.length;

                // Track overall word instances
                if (!searchResults.wordInstances[term]) {
                  searchResults.wordInstances[term] = {
                    totalOccurrences: 0,
                    pagesFound: [],
                  };
                }
                searchResults.wordInstances[term].totalOccurrences += termMatches.length;
                if (!searchResults.wordInstances[term].pagesFound.includes(page.pageNumber)) {
                  searchResults.wordInstances[term].pagesFound.push(page.pageNumber);
                }
              }
            });
          }
          break;

        case 'sentence':
          // Sentence search (exact sentence match)
          const normalizedSearchTerm = searchTerm.toLowerCase().trim().replace(/\s+/g, ' ');
          matches = pageTextLower.match(new RegExp(escapeRegExp(normalizedSearchTerm), 'g')) || [];
          occurrences = matches.length;
          break;
      }

      totalWords += page.wordCount;

      if (occurrences > 0) {
        searchResults.pagesFound.push({
          pageNumber: page.pageNumber,
          occurrences: occurrences,
          preview: getPreview(page.text, searchTerm, searchMode),
          wordInstances: wordInstancesOnPage, // Include word instances for this page
        });
        searchResults.totalOccurrences += occurrences;
      }
    });

    searchResults.percentageOfPages = ((searchResults.pagesFound.length / searchResults.totalPages) * 100).toFixed(2);
    searchResults.occurrenceDensity = ((searchResults.totalOccurrences / totalWords) * 100).toFixed(4);

    setResults(searchResults);
  };

  // Helper function to escape special regex characters
  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const getPreview = (text, searchTerm, mode) => {
    const lowerText = text.toLowerCase();
    const lowerSearchTerm = searchTerm.toLowerCase();

    switch (mode) {
      case 'exact':
        // Exact match preview (current behavior)
        const exactIndex = lowerText.indexOf(lowerSearchTerm);
        if (exactIndex === -1) return '';

        const exactStart = Math.max(0, exactIndex - 50);
        const exactEnd = Math.min(text.length, exactIndex + searchTerm.length + 50);
        const exactPreview = text.substring(exactStart, exactEnd);

        return exactPreview.replace(new RegExp(`(${escapeRegExp(searchTerm)})`, 'gi'), '<mark>$1</mark>');

      case 'multiword':
        // Multi-word preview with comma-separated terms
        const terms = lowerSearchTerm
          .split(',')
          .map((term) => term.trim())
          .filter((term) => term.length > 0);
        if (terms.length === 0) return '';

        // Find the first occurrence of any term
        let firstIndex = -1;
        let firstTerm = '';

        for (const term of terms) {
          const termIndex = lowerText.indexOf(term);
          if (termIndex !== -1 && (firstIndex === -1 || termIndex < firstIndex)) {
            firstIndex = termIndex;
            firstTerm = term;
          }
        }

        if (firstIndex === -1) return '';

        const multiStart = Math.max(0, firstIndex - 50);
        const multiEnd = Math.min(text.length, firstIndex + 100);
        let multiPreview = text.substring(multiStart, multiEnd);

        // Highlight all search terms in the preview
        terms.forEach((term) => {
          multiPreview = multiPreview.replace(new RegExp(`\\b(${escapeRegExp(term)})\\b`, 'gi'), '<mark>$1</mark>');
        });

        return multiPreview;

      case 'sentence':
        // Sentence match preview
        const normalizedSearchTerm = lowerSearchTerm.trim().replace(/\s+/g, ' ');
        const sentenceIndex = lowerText.indexOf(normalizedSearchTerm);
        if (sentenceIndex === -1) return '';

        const sentenceStart = Math.max(0, sentenceIndex - 30);
        const sentenceEnd = Math.min(text.length, sentenceIndex + normalizedSearchTerm.length + 30);
        const sentencePreview = text.substring(sentenceStart, sentenceEnd);

        return sentencePreview.replace(new RegExp(`(${escapeRegExp(normalizedSearchTerm)})`, 'gi'), '<mark>$1</mark>');

      default:
        return '';
    }
  };

  const resetAnalysis = () => {
    setPdfFile(null);
    setPdfText([]);
    setResults(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      // Simulate file input change
      const fakeEvent = {
        target: {
          files: [droppedFile],
        },
      };
      handleFileUpload(fakeEvent);
    } else {
      setError('Please drop a valid PDF file');
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  return (
    <Container>
      <SpaceBetween size="l">
        {/* Header */}
        <Header variant="h1" description="Upload a PDF and search for specific keywords or sentences">
          PDF Keyword Analyzer
        </Header>

        {/* File Upload Area */}
        <Container>
          <div
            style={{
              border: '2px dashed',
              borderColor: pdfFile ? '#037f0c' : '#adb5bd',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center',
              backgroundColor: pdfFile ? '#f0fff4' : '#f8f9fa',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => !pdfFile && fileInputRef.current?.click()}
          >
            {pdfFile ? (
              <SpaceBetween size="m" direction="vertical" alignItems="center">
                <Box textAlign="center">
                  <span role="img" aria-label="File" style={{ fontSize: '24px' }}>
                    📄
                  </span>
                </Box>
                <div>
                  <Box variant="h3" color="text-status-success">
                    {pdfFile.name}
                  </Box>
                  <Box variant="small" color="text-status-success">
                    {(pdfFile.size / 1024 / 1024).toFixed(2)} MB • {pdfText.length} pages
                  </Box>
                </div>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    resetAnalysis();
                  }}
                  variant="icon"
                  iconName="close"
                />
              </SpaceBetween>
            ) : (
              <SpaceBetween size="m" direction="vertical" alignItems="center">
                <Box textAlign="center">
                  <span role="img" aria-label="Upload" style={{ fontSize: '32px' }}>
                    📤
                  </span>
                </Box>
                <Box variant="h3">Drop your PDF here or click to browse</Box>
                <Box variant="small" color="text-body-secondary">
                  Files are processed locally in your browser. Max size: 50MB
                </Box>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                  variant="primary"
                >
                  Select PDF File
                </Button>
              </SpaceBetween>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </div>

          {error && (
            <Box padding={{ top: 'l' }}>
              <Alert type="error" header="Error">
                {error}
              </Alert>
            </Box>
          )}
        </Container>

        {/* Loading State */}
        {loading && (
          <SpaceBetween size="m" direction="vertical" alignItems="center">
            <Spinner size="large" />
            <Box variant="p" color="text-body-secondary">
              Processing PDF... This may take a moment for large files.
            </Box>
          </SpaceBetween>
        )}

        {/* Search Section */}
        {pdfText.length > 0 && !loading && (
          <Container header={<Header variant="h2">Search Document</Header>}>
            <SpaceBetween size="m">
              <FormField
                label="Enter keyword or phrase to search"
                description="Search is case-insensitive and will find all occurrences of the term"
              >
                <SpaceBetween size="m" direction="horizontal">
                  <div style={{ flexGrow: 1 }}>
                    <Input
                      value={searchTerm}
                      onChange={({ detail }) => setSearchTerm(detail.value)}
                      placeholder={
                        searchMode === 'multiword'
                          ? 'Enter terms separated by commas (e.g., apple, orange, banana)'
                          : 'Enter search term...'
                      }
                      onKeyDown={(e) => e.detail.key === 'Enter' && searchKeyword()}
                    />
                  </div>
                  <Button onClick={searchKeyword} variant="primary" iconName="search">
                    Search
                  </Button>
                </SpaceBetween>
              </FormField>

              <FormField label="Search Mode" description="Select how you want to search for terms in the document">
                <SpaceBetween size="m" direction="horizontal">
                  <Button
                    onClick={() => setSearchMode('exact')}
                    variant={searchMode === 'exact' ? 'primary' : 'normal'}
                    iconName={searchMode === 'exact' ? 'check' : undefined}
                  >
                    Exact Match
                  </Button>
                  <Button
                    onClick={() => setSearchMode('multiword')}
                    variant={searchMode === 'multiword' ? 'primary' : 'normal'}
                    iconName={searchMode === 'multiword' ? 'check' : undefined}
                  >
                    Multi-Word Search
                  </Button>
                  <Button
                    onClick={() => setSearchMode('sentence')}
                    variant={searchMode === 'sentence' ? 'primary' : 'normal'}
                    iconName={searchMode === 'sentence' ? 'check' : undefined}
                  >
                    Sentence Search
                  </Button>
                </SpaceBetween>
              </FormField>

              <Box variant="small" color="text-body-secondary">
                {searchMode === 'exact' && 'Exact Match: Finds the exact phrase or word as entered.'}
                {searchMode === 'multiword' &&
                  'Multi-Word Search: Enter multiple terms separated by commas. Finds pages containing any of the specified terms.'}
                {searchMode === 'sentence' && 'Sentence Search: Finds exact sentences or phrases as entered.'}
              </Box>
            </SpaceBetween>
          </Container>
        )}
        {/* Results */}
        {results && (
          <SpaceBetween size="l">
            {/* Summary */}
            <Container header={<Header variant="h2">Analysis Summary</Header>}>
              <ColumnLayout columns={4} variant="text-grid">
                <div>
                  <Box variant="h1" color="text-status-info" textAlign="center">
                    {results.totalPages}
                  </Box>
                  <Box variant="small" color="text-body-secondary" textAlign="center">
                    Total Pages
                  </Box>
                </div>
                <div>
                  <Box variant="h1" color="text-status-success" textAlign="center">
                    {results.pagesFound.length}
                  </Box>
                  <Box variant="small" color="text-body-secondary" textAlign="center">
                    Pages with Matches
                  </Box>
                </div>
                <div>
                  <Box variant="h1" color="text-status-info" textAlign="center">
                    {results.totalOccurrences}
                  </Box>
                  <Box variant="small" color="text-body-secondary" textAlign="center">
                    Total Occurrences
                  </Box>
                </div>
                <div>
                  <Box variant="h1" color="text-status-info" textAlign="center">
                    {results.percentageOfPages}%
                  </Box>
                  <Box variant="small" color="text-body-secondary" textAlign="center">
                    Page Coverage
                  </Box>
                </div>
              </ColumnLayout>
            </Container>

            {/* Word Instances Summary for Multi-word Search */}
            {results.searchMode === 'multiword' && Object.keys(results.wordInstances).length > 0 && (
              <Container
                header={
                  <Header variant="h2" description="Individual word statistics for multi-word search">
                    Word Instances Summary
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  {Object.entries(results.wordInstances).map(([word, data]) => (
                    <Cards
                      key={word}
                      cardDefinition={{
                        header: (item) => (
                          <SpaceBetween size="xs" direction="horizontal">
                            <Box variant="h3">"{item.word}"</Box>
                            <StatusIndicator type="info">
                              {item.data.totalOccurrences}{' '}
                              {item.data.totalOccurrences === 1 ? 'occurrence' : 'occurrences'}
                            </StatusIndicator>
                          </SpaceBetween>
                        ),
                        sections: [
                          {
                            id: 'pages',
                            header: 'Found on Pages',
                            content: (item) => <Box>{item.data.pagesFound.sort((a, b) => a - b).join(', ')}</Box>,
                          },
                          {
                            id: 'coverage',
                            header: 'Page Coverage',
                            content: (item) => (
                              <Box>
                                {item.data.pagesFound.length} of {results.totalPages} pages (
                                {((item.data.pagesFound.length / results.totalPages) * 100).toFixed(1)}%)
                              </Box>
                            ),
                          },
                        ],
                      }}
                      cardsPerRow={[{ cards: 1 }, { minWidth: 500, cards: 2 }]}
                      items={[{ word, data }]}
                    />
                  ))}
                </SpaceBetween>
              </Container>
            )}

            {/* Detailed Results */}
            {results.pagesFound.length > 0 ? (
              <Container
                header={
                  <Header
                    variant="h2"
                    description={`Density: ${results.occurrenceDensity}% of total words | Mode: ${
                      results.searchMode === 'exact'
                        ? 'Exact Match'
                        : results.searchMode === 'multiword'
                          ? 'Multi-Word Search'
                          : 'Sentence Search'
                    }`}
                    actions={
                      <StatusIndicator type="success">
                        {results.totalOccurrences} {results.totalOccurrences === 1 ? 'match' : 'matches'} found
                      </StatusIndicator>
                    }
                  >
                    Search Results for "{results.keyword}"
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  {results.pagesFound.map((page, index) => (
                    <Cards
                      key={index}
                      cardDefinition={{
                        header: (item) => (
                          <SpaceBetween size="xs" direction="horizontal">
                            <Box variant="h3">Page {item.pageNumber}</Box>
                            <StatusIndicator type="info">
                              {item.occurrences} {item.occurrences === 1 ? 'occurrence' : 'occurrences'}
                            </StatusIndicator>
                          </SpaceBetween>
                        ),
                        sections: [
                          ...(results.searchMode === 'multiword' &&
                          page.wordInstances &&
                          Object.keys(page.wordInstances).length > 0
                            ? [
                                {
                                  id: 'wordBreakdown',
                                  header: 'Word Breakdown',
                                  content: (item) => (
                                    <SpaceBetween size="xs">
                                      {Object.entries(item.wordInstances).map(([word, count]) => (
                                        <Box key={word} variant="small">
                                          <strong>"{word}"</strong>: {count}{' '}
                                          {count === 1 ? 'occurrence' : 'occurrences'}
                                        </Box>
                                      ))}
                                    </SpaceBetween>
                                  ),
                                },
                              ]
                            : []),
                          {
                            id: 'preview',
                            header: 'Context Preview',
                            content: (item) => (
                              <div
                                style={{
                                  padding: '10px',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '4px',
                                  fontFamily: 'monospace',
                                  lineHeight: '1.5',
                                }}
                              >
                                {/* Sanitized preview text - removing HTML for security */}
                                {item.preview.replace(/<mark>/g, '**').replace(/<\/mark>/g, '**')}
                              </div>
                            ),
                          },
                        ],
                      }}
                      cardsPerRow={[{ cards: 1 }]}
                      items={[page]}
                    />
                  ))}
                </SpaceBetween>
              </Container>
            ) : (
              <Container>
                <SpaceBetween size="m" direction="vertical" alignItems="center">
                  <Box textAlign="center">
                    <StatusIndicator type="warning" iconAriaLabel="Warning">
                      No Matches Found
                    </StatusIndicator>
                  </Box>
                  <Box variant="p" color="text-status-warning" textAlign="center">
                    No occurrences of "{results.keyword}" were found in the document.
                  </Box>
                </SpaceBetween>
              </Container>
            )}

            {/* New Search Button */}
            <Box textAlign="center">
              <SpaceBetween size="xs" direction="horizontal">
                <Button onClick={resetAnalysis} variant="link">
                  Upload a Different PDF
                </Button>
                <Button
                  onClick={() => {
                    setSearchTerm('');
                    setResults(null);
                  }}
                  variant="primary"
                >
                  New Search
                </Button>
              </SpaceBetween>
            </Box>
          </SpaceBetween>
        )}

        {/* Information */}
        {pdfText.length > 0 && !results && !loading && (
          <Container header={<Header variant="h3">Document Information</Header>}>
            <ColumnLayout columns={3} variant="text-grid">
              <div>
                <Box variant="h3" color="text-status-info" textAlign="center">
                  {pdfText.length}
                </Box>
                <Box variant="small" color="text-body-secondary" textAlign="center">
                  Total Pages
                </Box>
              </div>
              <div>
                <Box variant="h3" color="text-status-info" textAlign="center">
                  {pdfText.reduce((sum, page) => sum + page.wordCount, 0).toLocaleString()}
                </Box>
                <Box variant="small" color="text-body-secondary" textAlign="center">
                  Total Words
                </Box>
              </div>
              <div>
                <Box variant="h3" color="text-status-info" textAlign="center">
                  {Math.round(pdfText.reduce((sum, page) => sum + page.wordCount, 0) / pdfText.length).toLocaleString()}
                </Box>
                <Box variant="small" color="text-body-secondary" textAlign="center">
                  Avg. Words per Page
                </Box>
              </div>
            </ColumnLayout>
          </Container>
        )}
      </SpaceBetween>
    </Container>
  );
});

export default PDFKeywordAnalyzer;
